import "./ProyectosCommon.css";
import { CosechaResponse } from "../../../shared/response/proyectos/CosechaResponse";
import { CosechaService } from "../../services/proyectos/CosechaService";
import { CultivoService } from "../../services/proyectos/CultivoService";
import { CatalogoService } from "../../services/proyectos/CatalogoService";
import { CosechaFormModal } from "./CosechaFormModal";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtPct, fmtFechaProy } from "../../../shared/utils/proyectosFormat";

/** Panel de temporadas + tasa IPC (base y por categoría), montado dentro de ParametrosPage. Antes era CosechasPage completa. */
export class CosechasPanel {
  private _all: CosechaResponse[] = [];
  private _categorias: string[] = [];
  private _tbody!: HTMLTableSectionElement;

  public render(container: HTMLElement): void {
    container.innerHTML = `
      <div class="pr-pg-hd">
        <div>
          <div class="pr-pg-ti">Cosechas</div>
          <div class="pr-pg-su">Temporadas y su tasa IPC, base y por categoría. La vigente fija el precio del catálogo ahora mismo; se congela al activar el primer Macro-Proyecto.</div>
        </div>
        <button class="sx-btn sx-btn-primary sx-btn-sm" id="cos-nuevo"><i class="ph ph-plus"></i> Nueva cosecha</button>
      </div>

      <div class="pr-tbl-wrap">
        <table class="pr-tbl">
          <thead>
            <tr><th>Cosecha</th><th>IPC base</th><th>Vigencia</th><th>Macro-Proyectos</th><th>Proyectos</th><th>Vigente</th><th>Estado</th><th>Acciones</th></tr>
          </thead>
          <tbody id="cos-tbody"><tr><td colspan="8" class="pr-empty">Cargando…</td></tr></tbody>
        </table>
      </div>
    `;

    this._tbody = container.querySelector("#cos-tbody")! as HTMLTableSectionElement;
    container.querySelector<HTMLButtonElement>("#cos-nuevo")!.onclick = () => this._onCreate();
    void this._loadData();
  }

  private async _loadData(): Promise<void> {
    try {
      const [items, cultivos] = await Promise.all([
        CosechaService.instance.get(),
        CultivoService.instance.get().catch(() => []),
      ]);
      this._all = items.filter((c) => !c.isEliminado).sort((a, b) => b.anio - a.anio);

      // No hay endpoint de "todas las categorías" -el catálogo es por cultivo-, así que se
      // recolectan las categorías reales de todos los cultivos activos.
      const porCultivo = await Promise.all(
        cultivos.filter((c) => !c.isEliminado).map((c) => CatalogoService.instance.getByCultivoId(c.id).catch(() => [])),
      );
      const set = new Set<string>();
      porCultivo.flat().forEach((it) => { if (it.categoria) set.add(it.categoria); });
      this._categorias = [...set].sort((a, b) => a.localeCompare(b));

      this._renderRows();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar cosechas", (error as Error).message);
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Error al cargar</td></tr>`;
    }
  }

  private _renderRows(): void {
    if (this._all.length === 0) {
      this._tbody.innerHTML = `<tr><td colspan="8" class="pr-empty">Sin cosechas. Crea la primera.</td></tr>`;
      return;
    }
    this._tbody.innerHTML = this._all.map((c, i) => `
      <tr>
        <td style="font-weight:var(--sx-weight-semibold)">${this._esc(c.nombre)}</td>
        <td class="pr-num">${fmtPct(c.tasaBaseIpc)}${c.tasasPorCategoria.some((t) => t.override) ? ` <span class="pr-pill am" style="font-size:10px">${c.tasasPorCategoria.filter((t) => t.override).length} ajustadas</span>` : ""}</td>
        <td style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">
          ${c.fechaInicioVigencia ? fmtFechaProy(c.fechaInicioVigencia) : "—"} – ${c.fechaFinVigencia ? fmtFechaProy(c.fechaFinVigencia) : "—"}
        </td>
        <td class="pr-num">${c.totalMacroProyectos}</td>
        <td class="pr-num">${c.totalProyectos}</td>
        <td>
          ${c.vigente
            ? `<span class="pr-pill blue"><i class="ph ph-check-circle"></i> Vigente</span>`
            : `<button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="vigente" data-idx="${i}">Marcar vigente</button>`}
        </td>
        <td>
          ${c.frozen ? `<span class="pr-pill na"><i class="ph ph-lock-simple"></i> Congelada</span>` : `<span class="pr-pill ok"><i class="ph ph-pencil-simple"></i> Editable</span>`}
        </td>
        <td>
          <div class="pr-actions">
            ${c.frozen ? "" : `<button class="sx-btn sx-btn-secondary sx-btn-sm" data-act="activar" data-idx="${i}">Congelar</button>`}
            ${c.frozen ? "" : `<button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-act="editar" data-idx="${i}" title="Editar"><i class="ph ph-pencil-simple"></i></button>`}
          </div>
        </td>
      </tr>`).join("");

    this._tbody.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((btn) => {
      const c = this._all[Number(btn.dataset.idx)];
      btn.onclick = () => {
        if (btn.dataset.act === "editar")  this._onEdit(c);
        if (btn.dataset.act === "activar") void this._onActivar(c);
        if (btn.dataset.act === "vigente") void this._onMarcarVigente(c);
      };
    });
  }

  private _onCreate(): void {
    new CosechaFormModal(this._categorias, (saved) => {
      this._all = [saved, ...this._all].sort((a, b) => b.anio - a.anio);
      this._renderRows();
    }).init();
  }

  private _onEdit(c: CosechaResponse): void {
    new CosechaFormModal(this._categorias, (saved) => {
      this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
      this._renderRows();
    }, c).init();
  }

  private async _onActivar(c: CosechaResponse): Promise<void> {
    new ConfirmDialogComponent({
      title: "Congelar cosecha",
      message: `¿Congelar las tasas IPC de "${c.nombre}"? A partir de ahí no se van a poder editar, ni la tasa base ni las tasas por categoría.`,
      confirmLabel: "Congelar",
      onConfirm: async () => {
        try {
          const saved = await CosechaService.instance.activar(c.id);
          this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
          this._renderRows();
          errorMessageComponent.addSuccess("Cosecha congelada", `Las tasas de "${c.nombre}" ya están fijas.`);
        } catch (error) {
          errorMessageComponent.addError("Error al congelar", (error as Error).message);
        }
      },
    }).init();
  }

  private async _onMarcarVigente(c: CosechaResponse): Promise<void> {
    new ConfirmDialogComponent({
      title: "Marcar como vigente",
      message: `¿Marcar "${c.nombre}" como la cosecha vigente? A partir de ahora fija el precio del catálogo (base + ajustes por categoría) y el de todo proyecto nuevo, en lugar de la que era vigente hasta ahora.`,
      confirmLabel: "Marcar vigente",
      onConfirm: async () => {
        try {
          await CosechaService.instance.marcarVigente(c.id);
          // El servidor desmarca la anterior vigente: se resincroniza la lista completa en vez
          // de un upsert puntual para que ese cambio también se refleje.
          this._all = (await CosechaService.instance.get()).filter((x) => !x.isEliminado).sort((a, b) => b.anio - a.anio);
          this._renderRows();
          errorMessageComponent.addSuccess("Cosecha vigente actualizada", `"${c.nombre}" ya es la cosecha vigente.`);
        } catch (error) {
          errorMessageComponent.addError("Error al marcar como vigente", (error as Error).message);
        }
      },
    }).init();
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
